// @ts-nocheck
import React, { useCallback, useEffect, useState } from 'react';
import classNames from 'classnames';
import { useHistory, useLocation } from 'react-router-dom';

import Web3WalletAuthPanel from '../containers/AuthenticationPage/Web3WalletAuthPanel/Web3WalletAuthPanel';
import { FormattedMessage, useIntl } from '../util/reactIntl';
import { requestProgressiveAuth } from '../util/authIntent';
import { claimProxyGig, fetchNoticeboardFeed } from '../util/api';
import { connectInjectedEvmWallet } from '../util/web3Auth';
import { useRouteConfiguration } from '../context/routeConfigurationContext';
import { createResourceLocatorString } from '../util/routes';

import GigStatusBadge from './GigStatusBadge/GigStatusBadge';
import { PrimaryButton } from './Button/Button';
import IconSpinner from './IconSpinner/IconSpinner';
import { noticeboardSection } from '../config/marketplaceContent';

import css from './NoticeboardFeed.module.css';

const POLL_INTERVAL_MS = 5000;

export interface NoticeboardFeedItem {
  notice_id: string;
  merchant_id: string;
  gig_type: string;
  payload: {
    title: string;
    description?: string;
    location: { lat: number; lng: number };
    escrow_amount: number;
    deadline: string;
  };
  created_at: string;
}

interface NoticeboardFeedProps {
  isAuthenticated?: boolean;
  className?: string;
  rootClassName?: string;
  /** When true, card taps do not open auth — only Claim does. */
  deferAuthUntilClaim?: boolean;
}

const buildMapsUrl = (lat: number, lng: number) =>
  `https://www.google.com/maps?q=${encodeURIComponent(`${lat},${lng}`)}`;

const formatEscrowAmount = (amount: number) => {
  if (!Number.isFinite(amount)) {
    return '';
  }
  return `${amount % 1 === 0 ? amount.toFixed(0) : amount.toFixed(2)} USDC`;
};

const NoticeboardFeed: React.FC<NoticeboardFeedProps> = ({
  isAuthenticated = false,
  className = null,
  rootClassName = null,
  deferAuthUntilClaim = false,
}) => {
  const intl = useIntl();
  const history = useHistory();
  const location = useLocation();
  const routeConfiguration = useRouteConfiguration();

  const [gigs, setGigs] = useState<NoticeboardFeedItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [claimingId, setClaimingId] = useState<string | null>(null);
  const [claimError, setClaimError] = useState<string | null>(null);
  const [walletModalOpen, setWalletModalOpen] = useState(false);
  const [pendingClaimId, setPendingClaimId] = useState<string | null>(null);

  const loadFeed = useCallback(() => {
    setError(null);
    return fetchNoticeboardFeed()
      .then((items: NoticeboardFeedItem[]) => {
        setGigs(Array.isArray(items) ? items : []);
      })
      .catch((err: Error) => {
        setError(err.message || 'Failed to load noticeboard feed');
      })
      .finally(() => {
        setLoading(false);
      });
  }, []);

  useEffect(() => {
    loadFeed();
    const intervalId = window.setInterval(loadFeed, POLL_INTERVAL_MS);
    return () => window.clearInterval(intervalId);
  }, [loadFeed]);

  const routeToCockpit = (noticeId: string) => {
    history.push(
      createResourceLocatorString('RunnerCockpitPage', routeConfiguration, { noticeId }, {})
    );
  };

  const executeClaim = async (noticeId: string, runnerWallet?: string) => {
    await claimProxyGig(noticeId, { runnerWallet });
    routeToCockpit(noticeId);
  };

  const handleClaimGig = async (noticeId: string) => {
    const proceed = async () => {
      setClaimingId(noticeId);
      setClaimError(null);

      try {
        let runnerWallet: string | undefined;
        try {
          const { walletAddress } = await connectInjectedEvmWallet();
          runnerWallet = walletAddress;
        } catch (walletError) {
          setPendingClaimId(noticeId);
          setWalletModalOpen(true);
          return;
        }

        await executeClaim(noticeId, runnerWallet);
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Could not claim gig';
        setClaimError(message);
      } finally {
        setClaimingId(null);
      }
    };

    await requestProgressiveAuth({
      history,
      routeConfiguration,
      location,
      intent: 'claimGig',
      userType: 'provider',
      onAuthenticated: proceed,
    });
  };

  const handleWalletLinked = async () => {
    setWalletModalOpen(false);
    if (!pendingClaimId) {
      return;
    }

    const noticeId = pendingClaimId;
    setPendingClaimId(null);
    setClaimingId(noticeId);
    setClaimError(null);

    try {
      const { walletAddress } = await connectInjectedEvmWallet();
      await executeClaim(noticeId, walletAddress);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Could not claim gig';
      setClaimError(message);
    } finally {
      setClaimingId(null);
    }
  };

  const handleFeedItemActivate = (noticeId: string) => {
    if (deferAuthUntilClaim) {
      // Quiet browse mode: ignore card taps; Claim button is the only auth gate.
      return;
    }
    requestProgressiveAuth({
      history,
      routeConfiguration,
      location,
      intent: 'claimGig',
      userType: 'provider',
      onAuthenticated: () => {
        setPendingClaimId(noticeId);
        setWalletModalOpen(true);
      },
    });
  };

  return (
    <>
      <section
        className={classNames(rootClassName || css.root, className)}
        data-testid="noticeboard-feed"
      >
        <header className={css.header}>
          <div>
            <h2 className={css.title}>{noticeboardSection.title}</h2>
            <p className={css.subtitle}>{noticeboardSection.subtitle}</p>
          </div>
          <span className={css.liveBadge}>{noticeboardSection.liveBadge}</span>
        </header>

        {error ? <p className={css.error}>{error}</p> : null}
        {claimError ? <p className={css.error}>{claimError}</p> : null}

        <div className={css.feed}>
          {loading && gigs.length === 0 ? (
            <div className={css.loading}>
              <IconSpinner />
            </div>
          ) : gigs.length === 0 ? (
            <div className={css.emptyState}>
              <h3 className={css.emptyTitle}>{noticeboardSection.emptyTitle}</h3>
              <p className={css.emptyBody}>{noticeboardSection.emptyBody}</p>
            </div>
          ) : (
            <ul className={css.list}>
              {gigs.map(gig => {
                const { payload } = gig;
                const lat = payload?.location?.lat;
                const lng = payload?.location?.lng;
                const hasLocation = Number.isFinite(lat) && Number.isFinite(lng);

                return (
                  <li key={gig.notice_id} className={css.item} data-testid="noticeboard-feed-item">
                    <div
                      className={css.itemBody}
                      onClick={() => handleFeedItemActivate(gig.notice_id)}
                      onKeyDown={e => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          handleFeedItemActivate(gig.notice_id);
                        }
                      }}
                      role="button"
                      tabIndex={0}
                    >
                      <GigStatusBadge status="FUNDED" className={css.statusFunded} />
                      <p className={css.gigType}>{gig.gig_type}</p>
                      <h3 className={css.itemTitle}>{payload?.title || 'Untitled gig'}</h3>
                      {hasLocation ? (
                        <a
                          className={css.locationLink}
                          href={buildMapsUrl(lat, lng)}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          {intl.formatMessage(
                            { id: 'NoticeboardFeed.locationLink' },
                            { lat, lng }
                          )}
                        </a>
                      ) : null}
                      {payload?.escrow_amount != null ? (
                        <p className={css.escrow}>
                          {intl.formatMessage(
                            { id: 'NoticeboardFeed.escrowAmount' },
                            { amount: formatEscrowAmount(Number(payload.escrow_amount)) }
                          )}
                        </p>
                      ) : null}
                    </div>
                    <PrimaryButton
                      className={css.claimButton}
                      onClick={() => handleClaimGig(gig.notice_id)}
                      inProgress={claimingId === gig.notice_id}
                      disabled={Boolean(claimingId)}
                      data-testid="noticeboard-claim-gig"
                    >
                      <FormattedMessage id="NoticeboardFeed.claimGigButton" />
                    </PrimaryButton>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </section>

      {walletModalOpen ? (
        <div
          className={css.modalOverlay}
          role="dialog"
          aria-modal="true"
          data-testid="noticeboard-wallet-modal"
        >
          <div className={css.modal}>
            <h3 className={css.modalTitle}>{noticeboardSection.walletInterceptTitle}</h3>
            <p className={css.modalBody}>{noticeboardSection.walletInterceptBody}</p>
            <Web3WalletAuthPanel onAuthenticated={handleWalletLinked} />
          </div>
        </div>
      ) : null}
    </>
  );
};

export default NoticeboardFeed;
